import { t as translateDate } from './translate-date.js';
import './index2.js';

/**
 * **Atlantis ORS **
 *
 * @exports bs_311
 * @category engines
 */
function factory({ window, document, thn }) {

  const engine = {};

  /**
     * @memberOf module:bs_311
     * @see {@link prestart}
     *
     * @param {object} ctx - The context object.
     */
  engine.boot = function boot() {
    // This BE has 2 pages of R&R in their funnel. First one does not have input for guests, so we ignore it in our page mappings.
    // The Page does not refresh when we get to the second one, so we use a boot observer
    const container = document.querySelector('.cart-charges-details');

    if (container && !thn.atlantisObs) {
      thn.atlantisObs = thn.debounce(function thnAtlantisObs() {
        thn.pageCheck();
      }, 400);
    }

    thn.observeDom(container, thn.atlantisObs, {
      attributes: true,
    });

    thn.shutdown(function () {
      thn.stopObserveDom(container, thn.atlantisObs);
      delete thn.atlantisObs;
    });
  };

  /**
     * @memberOf module:bs_311
     * @see {@link language_check}
     *
     * @param {object} ctx - The context object.
     */
  engine.languageCheck = function languageCheck(ctx) {

    return new Promise(function (resolve, reject) {

      const aux = ctx.getHtmlLang();

      if (aux && ctx.langs.test(aux)) {
        resolve(aux);
        return;
      }

      resolve('en');
    });
  };

  /**
   * @memberOf module:bs_311
   * @see {@link generate_search_url}
   *
   * @param {object} ctx - The context object.
   */
  engine.generateSearchUrl = function generateSearchUrl(ctx) {
    const url = new URL(ctx.baseUrl);
    const validUrl = url.hostname.includes('booking');

    if (!validUrl) {
      ctx.newUrl = '';
      return ctx;
    }

    ctx.newUrl = url.toString();
    return ctx;
  };

  /**
     * @memberOf module:bs_311
     * @see {@link onStateChange}
     *
     * @param {object} ctx - The context object.
     */
  engine.onStateChange = function onStateChange(ctx) {
    thn.pageCheck();
  };

  /**
     * @memberOf module:bs_311
     * @this thn.widgets.price
     * @see {@link Price#observe_dom}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceObserveDom = function priceObserveDom(ctx) {
    const container = document.querySelector('.booking-summary-sidebar');
    const loader = document.querySelector('#pre_loader_block');

    if (container && !thn.atlantisObserver) {

      thn.atlantisObserver = thn.debounce(() => {
        if (!thn.isVisible(loader)) {
          ctx.widget.refresh();
        }
      }, 400);

      thn.observeDom(container, thn.atlantisObserver, {
        childList: true,
      });

      thn.pageUnload(() => {
        thn.stopObserveDom(container, thn.atlantisObserver);
        delete thn.atlantisObserver;
      });
    }
  };

  /**
     * @memberOf module:bs_311
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData = function priceParseData(ctx) {

    const widgetData = {};

    // Dates
    // url param has this format: check_in_date=Oct 21, 2020
    const dateInDataLayer = thn.objectGet(window.dataLayer, 'checkin');
    const dateOutDataLayer = thn.objectGet(window.dataLayer, 'checkout');

    // Sometimes the url param has this format: check_in_date=11/26/2020, in which case:
    const datesText = document.querySelector('div#summary > div.panel-default > div.panel-heading > h4.panel-title')?.innerText;
    const datesArray = datesText.split('-');

    if (dateInDataLayer && dateOutDataLayer) {
      widgetData.dateBegin = translateDate(dateInDataLayer, 'en');
      widgetData.dateEnd = translateDate(dateOutDataLayer, 'en');

    } else if (datesArray) {
      widgetData.dateBegin = translateDate(datesArray[0], thn.data.language);
      widgetData.dateEnd = translateDate(datesArray[1], thn.data.language);
    }

    // Guests, Adults & Children;
    // Second page of R&R with guests input and a tab per rooms
    // The client chose to not consider the first R&R page as it doesn't have input for guests,only dates
    // and chose to get the maximum occupancy of the cheapest room to compare with OTA prices
    if (!thn.empty(document.querySelector('.cart-charges-details').style.display)) {
      const firstRoomEl = document.querySelector('.room-click');
      const firstRoom = firstRoomEl && firstRoomEl.getAttribute('id');
      const roomSelector = firstRoom && document.querySelector(`#Room_Type_qty_selection_block${firstRoom}`);
      const adults = roomSelector && roomSelector.querySelectorAll('select.adult-select option');
      const children = roomSelector && roomSelector.querySelectorAll('select.child-select option');
      if (adults.length > 0 || children.length > 0) {
        const adultsArray = Array.from(adults).map((elm) => elm.value);
        const childrenArray = Array.from(children).map((elm) => elm.value);
        widgetData.adults = Math.max(...adultsArray);
        widgetData.children = Math.max(...childrenArray);
      }
    } else {
      widgetData.adults = 2;
      widgetData.children = 0;
    }

    widgetData.rooms = 1;
    widgetData.guests = widgetData.adults + widgetData.children;

    // Prices & Currency || No Rooms
    // PHP amounts are formatted ("2,851.32"): we use thn.parseNumber
    // any other currency is just a float string ("54.46021"): we use built-in parseFloat
    const currency = thn.parseCurrency(document.querySelector('.currency-symbol')?.textContent);
    const mapperFn = currency === 'PHP' ? thn.parseNumber : parseFloat;
    const prices = [...document.querySelectorAll('.room-rates .currency-amount')]
      .map((el) => mapperFn(el?.textContent))
      .filter(Boolean);

    if (prices && prices.length) {
      widgetData.price = Math.min(...prices);
      widgetData.currency = currency;
      widgetData.set = true;
    } else {
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  /**
     * @memberOf module:bs_311
     * @this thn.widgets.booking
     * @see {@link Booking#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingParseData = function bookingParseData(ctx) {

    const widgetData = {};

    const dateInSelector = document.querySelectorAll('.reservation-details');
    let dateIn;
    let dateOut;

    if (dateInSelector.length > 1) {
      dateIn = (((dateInSelector[1].querySelectorAll('div.row-mobile div.col-xs-6')[1] || {}).textContent || '').match(/(\d+)\D+(\d+)\D+(\d+)/) || [])[0].replace(/-/g, '/');
      dateOut = (((dateInSelector[1].querySelectorAll('div.row-mobile div.col-xs-6')[3] || {}).textContent || '').match(/(\d+)\D+(\d+)\D+(\d+)/) || [])[0].replace(/-/g, '/');
    }

    if (dateIn && dateOut) {
      widgetData.dateBegin = dateIn;
      widgetData.dateEnd = dateOut;
    }

    const bookingId = thn.dataLayerHelper.get('confirmation_id') || document.querySelector('.reservation-details-title').textContent.trim();
    const price = ((thn.dataLayerHelper.get('price') || '').match(/\d+\.\d{2}/) || '').toString();
    const currency = thn.dataLayerHelper.get('currency');
    widgetData.bookingId = bookingId;
    widgetData.price = price && thn.parseNumber(price);
    widgetData.currency = currency && thn.parseCurrency(currency);

    return widgetData;
  };

  return engine;
}

export { factory };
